import React, { useState, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { cn } from '../../lib/utils';
import { notificationApi } from '../../services/notificationApi';
import type { NotificationSummary } from '../../types';

interface NotificationBellProps {
  onClick?: () => void;
}

export function NotificationBell({ onClick }: NotificationBellProps) {
  const [summary, setSummary] = useState<NotificationSummary | null>(null);
  const [, setLoading] = useState(true);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const data = await notificationApi.getSummary();
        setSummary(data);
      } catch (error) {
        console.error('Failed to fetch notification summary:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
    // Poll every 30 seconds
    const interval = setInterval(fetchSummary, 30000);
    return () => clearInterval(interval);
  }, []);

  const unreadCount = summary?.total_unread ?? 0;
  const hasUrgent = (summary?.urgent_count ?? 0) > 0;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClick}
          className="relative h-9 w-9"
        >
          <Bell className={cn("h-5 w-5", hasUrgent && "text-warning")} />
          {unreadCount > 0 && (
            <span
              className={cn(
                "absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[10px] font-bold text-white",
                hasUrgent ? "bg-destructive animate-pulse" : "bg-primary"
              )}
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        <p>{unreadCount > 0 ? `${unreadCount} 条未读通知` : '没有未读通知'}</p>
      </TooltipContent>
    </Tooltip>
  );
}
